import React from 'react';
import { MultiSelectDropdownLayoutProps, Option } from 'ui-builder';
import Select, { ActionMeta, components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { ChevronDown } from 'lucide-react';
import { cn } from 'shadcn-ui';
import ShadcnBaseInputLayout from '../../../form/layout/ShadcnBaseInputLayout';

export default function ShadcnMultiSelectDropdownLayout<
  ValueType extends string | number,
>(props: MultiSelectDropdownLayoutProps<ValueType>) {
  const Widget = props.optionCreateFunc ? CreatableSelect : Select;

  const currentValue = props.value || [];
  const getValue = (): Option<ValueType>[] => {
    return currentValue
      .map((id) => props.options.find((v) => v.value === id)!)
      .filter(Boolean)
      .map((option) => {
        const item = { ...option };

        if (
          props.notEditableIds &&
          props.notEditableIds.includes(option.value)
        ) {
          item.isFixed = true;
        }
        return item;
      });
  };

  return (
    <ShadcnBaseInputLayout
      label={props.label}
      name={props.name}
      errors={props.errors}
      hint={props.hint}
      showLabel={props.showLabel}
      className={props.className}
      hideLabel={props.hideLabel}
      showRequiredIcon={props.showRequiredIcon}
    >
      <Widget<Option<ValueType>, true>
        name={props.name}
        options={props.options}
        unstyled
        menuPlacement="auto"
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999, pointerEvents: 'auto' as const }) }}
        classNames={{
          menuPortal: () => 'nekst-dropdown-portal',
          control: (state) =>
            cn(
              '!min-h-9 w-full min-w-0 !rounded-full !border !border-gray-200 !bg-gray-100 !pl-3 !pr-3 !py-1 !text-sm !shadow-none !outline-none flex items-center flex-wrap cursor-pointer',
              state.isFocused && '!border-blue-500',
              state.isDisabled && 'opacity-50 cursor-not-allowed',
            ),
          placeholder: () => 'text-input-placeholder truncate text-sm',
          menu: () =>
            'bg-popover text-popover-foreground font-sans mt-1 rounded-md border border-gray-200 shadow-md z-50 overflow-hidden',
          menuList: () => 'p-1 max-h-60 overflow-y-auto',
          option: (state) =>
            cn(
              'relative flex w-full cursor-default items-center rounded-sm py-2 pl-3 pr-8 !text-sm text-slate-700 select-none',
              state.isFocused && 'bg-gray-100 text-gray-700',
              state.isSelected && 'font-medium',
              state.isDisabled && 'pointer-events-none opacity-50',
            ),
          multiValue: (state) =>
            cn(
              'rounded-full px-2 py-0.5 text-xs font-medium flex items-center gap-1',
              state.data.isFixed
                ? 'bg-gray-200 text-gray-500'
                : 'bg-primary/10 text-primary',
            ),
          multiValueLabel: () => 'text-xs',
          multiValueRemove: (state) =>
            cn(
              'hover:text-primary/70 cursor-pointer rounded-full',
              state.data.isFixed && 'hidden',
            ),
          valueContainer: () => 'gap-1 px-0 flex flex-wrap',
          dropdownIndicator: () => 'text-gray-400 p-0 pr-1',
          indicatorsContainer: () => 'gap-1',
          input: () =>
            'text-sm text-gray-700 [&_input]:!border-none [&_input]:!outline-none [&_input]:!shadow-none [&_input]:!h-auto [&_input]:!p-0 [&_input]:!caret-transparent',
          noOptionsMessage: () => 'text-sm text-gray-400 py-2 px-3',
          loadingIndicator: () => 'text-gray-400 p-0',
        }}
        isClearable={false}
        isDisabled={props.isLoading}
        isLoading={props.isLoading}
        value={getValue()}
        placeholder={props.placeholder}
        isMulti
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        onChange={(newValue: any, event: ActionMeta<Option<ValueType>>) => {
          const option = event.option || event.removedValue;

          if (option) {
            if (event.action === 'select-option') {
              props.addItemFunc(option.value);
            } else if (event.action === 'deselect-option') {
              props.removeItemFunc(option.value);
            } else if (event.action === 'remove-value') {
              props.removeItemFunc(option.value);
            }
          }
        }}
        onCreateOption={props.optionCreateFunc}
        isOptionDisabled={() =>
          !!props.max && currentValue.length >= props.max
        }
        onMenuClose={() => undefined}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: (indicatorProps) => (
            <components.DropdownIndicator {...indicatorProps}>
              <ChevronDown className="size-4 opacity-50" />
            </components.DropdownIndicator>
          ),
        }}
      />
    </ShadcnBaseInputLayout>
  );
}
